import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { webAlert, webConfirm } from "../webDialog";
import { adminContentApi, GeneratedVideo } from "../../api/content";
import { useAuthStore } from "../../state/authStore";
import { Icon } from "../components/Icon";
import { WebMascot } from "../components/WebMascot";
import { getApiBaseUrl } from "../../api/client";
import { extractFrames, loadVideoBlobUrl } from "./frameExtraction";

// ─────────────────────────────────────────────────────────────────────────
// WebVideoDetailsEditor — the post-generation "finish your video" modal.
//
// Auto-opens when a generation transitions to `done` so the parent lands
// straight on the finalize step: better title, description, poster picker
// (upload OR pick a video frame), category chips, age-range sliders. A
// big call-to-action "Edit questions" link drops them into the question
// editor when they're ready.
//
// All saves hit the new PATCH /v1/admin/videos/:id + POST/DELETE thumb
// endpoints. The host (WebStudio) merges the returned `GeneratedVideo`
// back into its local generations state via `onApply`.
// ─────────────────────────────────────────────────────────────────────────

// Keep in sync with the chips on WebHome's Browse the catalog row. Lower-
// cased on submit so the backend stores a normalized set.
const CATEGORY_OPTIONS = [
  "Numbers",
  "Letters",
  "Colors",
  "Shapes",
  "Social Skills",
  "Language",
  "Bedtime",
  "Creativity",
];

const AGE_MIN_FLOOR = 0;
const AGE_MAX_CEIL = 12;

export type VideoDetailsDraft = {
  title: string;
  description: string;
  categories: string[];
  ageMin: number;
  ageMax: number;
};

type Props = {
  videoId: string;
  // Pre-fill values — typically the latest Generation from WebStudio.
  initialTitle: string;
  initialDescription: string | null;
  initialCategories: string[];
  initialAgeMin: number;
  initialAgeMax: number;
  initialThumbUrl: string;
  // Auth'd MP4 stream URL — drives the "Pick from video" poster path. When
  // empty, only the file-upload picker is available.
  videoUrl?: string;
  videoDurationSec?: number;
  onClose: () => void;
  // Fires after each successful PATCH / poster mutation with the refreshed
  // video. WebStudio merges this into its local generations state.
  onApply: (video: GeneratedVideo) => void;
  onPlay: () => void;
  onEditQuestions: () => void;
  onIterate: () => void;
};

export function WebVideoDetailsEditor({
  videoId,
  initialTitle,
  initialDescription,
  initialCategories,
  initialAgeMin,
  initialAgeMax,
  initialThumbUrl,
  videoUrl,
  videoDurationSec,
  onClose,
  onApply,
  onPlay,
  onEditQuestions,
  onIterate,
}: Props) {
  const { accessToken } = useAuthStore();

  const [title, setTitle] = useState(initialTitle);
  const [description, setDescription] = useState(initialDescription ?? "");
  const [categories, setCategories] = useState<string[]>(
    () => initialCategories.map((c) => c.toLowerCase()),
  );
  const [ageMin, setAgeMin] = useState(initialAgeMin);
  const [ageMax, setAgeMax] = useState(initialAgeMax);
  const [thumbUrl, setThumbUrl] = useState(initialThumbUrl);
  // Local preview (data URL) while the upload is in flight, so the poster
  // tile updates instantly without waiting for the round-trip.
  const [thumbPreview, setThumbPreview] = useState<string | null>(null);

  const [saving, setSaving] = useState(false);
  // Brief "Saved ✓" confirmation after a successful save (the modal stays open
  // on purpose so the parent can also set a poster / edit questions).
  const [justSaved, setJustSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [posterPickerOpen, setPosterPickerOpen] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const toggleCategory = (raw: string) => {
    const c = raw.toLowerCase();
    setCategories((prev) =>
      prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c].slice(0, 6),
    );
  };

  // Save the textual + array fields. Poster has its own endpoint and isn't
  // bundled here so the parent can save title/description partial progress
  // without re-uploading the image.
  const onSave = useCallback(async () => {
    if (!accessToken) return;
    const trimmedTitle = title.trim();
    if (!trimmedTitle) {
      setError("Title can't be empty.");
      return;
    }
    if (ageMin > ageMax) {
      setError("Min age can't be greater than max age.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const updated = await adminContentApi.updateVideo(
        videoId,
        {
          title: trimmedTitle,
          description: description.trim() ? description.trim() : null,
          categories,
          ageMin,
          ageMax,
        },
        accessToken,
      );
      onApply(updated);
      // Confirm, then close. It used to only flash "Saved ✓" and stay open on
      // the theory that the parent would upload a poster or edit questions
      // next — but the flash was cleared immediately by the effect below
      // (onApply feeds new props straight back in), so pressing Save looked
      // like it did nothing at all. Show the tick briefly so the click visibly
      // registers, then dismiss.
      setJustSaved(true);
      closeTimerRef.current = setTimeout(onClose, 650);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't save changes.");
    } finally {
      setSaving(false);
    }
  }, [accessToken, title, description, categories, ageMin, ageMax, videoId, onApply, onClose]);

  // Pending auto-close after a successful save — cleared if the parent
  // dismisses first, so we never call onClose on an unmounted modal.
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    },
    [],
  );

  // Any edit clears the "Saved ✓" flash so the button invites saving again.
  // Skipped on the first run: this effect fires on mount too, and onApply
  // pushes fresh props in right after a save, so an unguarded version wiped
  // the tick in the same commit that set it.
  const firstEditPass = useRef(true);
  useEffect(() => {
    if (firstEditPass.current) {
      firstEditPass.current = false;
      return;
    }
    setJustSaved(false);
  }, [title, description, categories, ageMin, ageMax]);

  const onUploadThumb = useCallback(
    async (dataUrl: string) => {
      if (!accessToken) return;
      setThumbPreview(dataUrl);
      try {
        const res = await adminContentApi.uploadVideoThumb(
          videoId,
          dataUrl,
          accessToken,
        );
        setThumbUrl(res.thumbUrl);
        // Re-fetch the updated video so the publication count etc. stay in
        // sync. updateVideo with no fields would also work but PATCH on
        // empty body is a no-op; PATCH with the same title is cheap and
        // returns the full row including the new thumbUrl.
        const refreshed = await adminContentApi.updateVideo(
          videoId,
          { title: title.trim() || initialTitle },
          accessToken,
        );
        onApply(refreshed);
      } catch (e) {
        webAlert(
          e instanceof Error ? e.message : "Couldn't upload poster.",
        );
        setThumbPreview(null);
      }
    },
    [accessToken, videoId, title, initialTitle, onApply],
  );

  const onRemoveThumb = useCallback(async () => {
    if (!accessToken) return;
    const ok = await webConfirm("Remove this poster?", { confirmLabel: "Confirm", destructive: true });
    if (!ok) return;
    try {
      await adminContentApi.removeVideoThumb(videoId, accessToken);
      setThumbUrl("");
      setThumbPreview(null);
      const refreshed = await adminContentApi.updateVideo(
        videoId,
        { title: title.trim() || initialTitle },
        accessToken,
      );
      onApply(refreshed);
    } catch (e) {
      webAlert(e instanceof Error ? e.message : "Couldn't remove poster.");
    }
  }, [accessToken, videoId, title, initialTitle, onApply]);

  const onPickFile = () => fileRef.current?.click();
  const onFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      if (fileRef.current) fileRef.current.value = "";
      const reader = new FileReader();
      reader.onload = () => {
        const result = String(reader.result ?? "");
        if (!result.startsWith("data:image/")) {
          webAlert("Please pick a PNG, JPEG, or WebP image.");
          return;
        }
        onUploadThumb(result);
        setPosterPickerOpen(false);
      };
      reader.onerror = () => webAlert("Couldn't read that file.");
      reader.readAsDataURL(file);
    },
    [onUploadThumb],
  );

  // Resolve the current poster src for <img>. We prefer the optimistic
  // data-URL preview during upload, then fall back to the persisted URL.
  const posterSrc = useMemo(() => {
    if (thumbPreview) return thumbPreview;
    if (!thumbUrl) return null;
    return thumbUrl.startsWith("http")
      ? thumbUrl
      : `${getApiBaseUrl()}${thumbUrl}`;
  }, [thumbPreview, thumbUrl]);

  // Escape always closes. The click targets can be covered or scrolled out of
  // view depending on content height, so this is the one dismissal path that
  // can't be blocked by layout.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="ws-modal" onClick={onClose}>
      <div
        className="ws-modal-card wvd-card"
        onClick={(e) => e.stopPropagation()}
      >
        <button className="ws-modal-close" onClick={onClose}>
          ×
        </button>

        <div className="wvd-grid">
          {/* Left: poster + actions */}
          <div className="wvd-poster-col">
            <div className="wvd-poster">
              {posterSrc ? (
                <img src={posterSrc} alt="Poster" />
              ) : (
                <div className="wvd-poster-empty">
                  <WebMascot mood="cheer" color="#fff" size={120} />
                </div>
              )}
              <button
                type="button"
                className="wvd-poster-play"
                onClick={onPlay}
                title="Play full video"
              >
                <Icon name="play" size={22} />
              </button>
            </div>
            <div className="wvd-poster-actions">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => setPosterPickerOpen((v) => !v)}
              >
                <Icon name="plus" size={14} />{" "}
                {posterSrc ? "Change poster" : "Set poster"}
              </button>
              {posterSrc && (
                <button
                  type="button"
                  className="btn btn-ghost wvd-danger"
                  onClick={onRemoveThumb}
                >
                  Remove
                </button>
              )}
            </div>

            {posterPickerOpen && (
              <PosterPicker
                videoId={videoId}
                videoUrl={videoUrl}
                onUploadDataUrl={(dataUrl) => {
                  onUploadThumb(dataUrl);
                  setPosterPickerOpen(false);
                }}
                onCancel={() => setPosterPickerOpen(false)}
                onPickFromFile={onPickFile}
              />
            )}
            <input
              ref={fileRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              style={{ display: "none" }}
              onChange={onFileChange}
            />

            {/* Action row — Edit questions / Iterate */}
            <div className="wvd-cta-row">
              <button className="btn" onClick={onEditQuestions}>
                <Icon name="sparkle" size={14} /> Edit questions
              </button>
              <button className="btn btn-ghost" onClick={onIterate}>
                <Icon name="sparkle" size={14} /> Iterate
              </button>
            </div>
          </div>

          {/* Right: editable fields */}
          <div className="wvd-fields">
            <div className="wvd-eyebrow">CUSTOM · FAMILY</div>
            <label className="wvd-field">
              <span className="wvd-field-label">Title</span>
              <input
                className="w-input"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Give this cartoon a name"
                maxLength={200}
              />
            </label>

            <label className="wvd-field">
              <span className="wvd-field-label">Description</span>
              <textarea
                className="w-input wvd-textarea"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                maxLength={2000}
                placeholder="What's this cartoon about? Kids and your future-self thank you."
              />
            </label>

            <div className="wvd-field">
              <span className="wvd-field-label">Categories</span>
              <div className="wvd-chip-row">
                {CATEGORY_OPTIONS.map((c) => {
                  const lc = c.toLowerCase();
                  const on = categories.includes(lc);
                  return (
                    <button
                      key={c}
                      type="button"
                      className={"wvd-chip " + (on ? "on" : "")}
                      onClick={() => toggleCategory(c)}
                    >
                      {c}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="wvd-field">
              <span className="wvd-field-label">
                Age range · {ageMin} – {ageMax}
              </span>
              <DualRange
                floor={AGE_MIN_FLOOR}
                ceil={AGE_MAX_CEIL}
                min={ageMin}
                max={ageMax}
                onChange={(lo, hi) => {
                  setAgeMin(lo);
                  setAgeMax(hi);
                }}
              />
            </div>

            {error && <div className="wvd-error">{error}</div>}

            <div className="wvd-actions">
              <button
                className="btn btn-ghost"
                onClick={onClose}
                disabled={saving}
              >
                Close
              </button>
              <button className="btn" onClick={onSave} disabled={saving}>
                {saving ? "Saving…" : justSaved ? "Saved ✓" : "Save changes"}
              </button>
            </div>
          </div>
        </div>
      </div>

      <WVDStyles />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// PosterPicker — same two-tab idea as the question option picker, but
// shrunk to the poster column. "Pick from video" extracts 6 frames; "From
// file" defers to the parent's file input (the host owns the <input>).
// ─────────────────────────────────────────────────────────────────────────
function PosterPicker({
  videoId,
  videoUrl,
  onUploadDataUrl,
  onCancel,
  onPickFromFile,
}: {
  videoId: string;
  videoUrl?: string;
  onUploadDataUrl: (dataUrl: string) => void;
  onCancel: () => void;
  onPickFromFile: () => void;
}) {
  const { accessToken } = useAuthStore();
  const [tab, setTab] = useState<"frames" | "generate" | "upload">(
    videoUrl ? "frames" : "generate",
  );
  // AI-generated poster candidates (upstream image generation — costs credits,
  // so they're fetched on demand, never automatically).
  const [posters, setPosters] = useState<string[]>([]);
  const [genLoading, setGenLoading] = useState(false);
  const [genErr, setGenErr] = useState<string | null>(null);
  const generate = async () => {
    if (!accessToken || genLoading) return;
    setGenLoading(true);
    setGenErr(null);
    try {
      const r = await adminContentApi.posterSuggestions(videoId, 3, accessToken);
      setPosters(r.posters);
    } catch (e) {
      setGenErr(
        e instanceof Error ? e.message : "Couldn't generate posters.",
      );
    } finally {
      setGenLoading(false);
    }
  };
  const [frames, setFrames] = useState<
    { tSec: number; dataUrl: string }[]
  >([]);
  const [loading, setLoading] = useState<boolean>(!!videoUrl);
  const [err, setErr] = useState<string | null>(null);
  const blobUrlRef = useRef<string | null>(null);

  useEffect(() => {
    if (tab !== "frames" || !videoUrl || !accessToken) return;
    let cancelled = false;
    setLoading(true);
    setErr(null);
    (async () => {
      let url: string | null = null;
      try {
        url = await loadVideoBlobUrl(videoUrl, accessToken);
        blobUrlRef.current = url;
        const out = await extractFrames(url, 6);
        if (cancelled) return;
        setFrames(out);
      } catch (e) {
        if (cancelled) return;
        setErr(
          e instanceof Error ? e.message : "Couldn't read video frames.",
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, [tab, videoUrl, accessToken]);

  return (
    <div className="wvd-poster-picker">
      <div className="wvd-picker-tabs">
        <button
          type="button"
          className={"wvd-picker-tab " + (tab === "frames" ? "active" : "")}
          onClick={() => setTab("frames")}
          disabled={!videoUrl}
        >
          From video
        </button>
        <button
          type="button"
          className={"wvd-picker-tab " + (tab === "generate" ? "active" : "")}
          onClick={() => setTab("generate")}
        >
          Generate
        </button>
        <button
          type="button"
          className={"wvd-picker-tab " + (tab === "upload" ? "active" : "")}
          onClick={() => setTab("upload")}
        >
          Upload
        </button>
        <div style={{ flex: 1 }} />
        <button
          type="button"
          className="wvd-picker-close"
          onClick={onCancel}
          aria-label="Close picker"
        >
          ×
        </button>
      </div>
      {tab === "frames" ? (
        !videoUrl ? (
          <div className="wvd-picker-empty">
            No video URL yet — try Upload.
          </div>
        ) : loading ? (
          <div className="wvd-frame-row">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="wvd-frame-tile loading" />
            ))}
          </div>
        ) : err ? (
          <div className="wvd-error">{err}</div>
        ) : (
          <div className="wvd-frame-row">
            {frames.map((f, i) => (
              <button
                key={i}
                type="button"
                className="wvd-frame-tile"
                onClick={() => onUploadDataUrl(f.dataUrl)}
              >
                <img src={f.dataUrl} alt={`Frame ${i + 1}`} />
              </button>
            ))}
          </div>
        )
      ) : tab === "generate" ? (
        genLoading ? (
          <div className="wvd-frame-row">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="wvd-frame-tile loading" />
            ))}
          </div>
        ) : genErr ? (
          <div className="wvd-error">{genErr}</div>
        ) : posters.length > 0 ? (
          <div className="wvd-frame-row">
            {posters.map((p, i) => (
              <button
                key={i}
                type="button"
                className="wvd-frame-tile"
                onClick={() => onUploadDataUrl(p)}
              >
                <img src={p} alt={`Poster option ${i + 1}`} />
              </button>
            ))}
          </div>
        ) : (
          <div className="wvd-upload-row">
            <button type="button" className="btn" onClick={generate}>
              <Icon name="sparkle" size={14} /> Generate 3 posters
            </button>
            <span className="wvd-upload-hint">
              Drawn from this video's prompt · takes ~30s
            </span>
          </div>
        )
      ) : (
        <div className="wvd-upload-row">
          <button
            type="button"
            className="btn"
            onClick={onPickFromFile}
          >
            <Icon name="plus" size={14} /> Choose image…
          </button>
          <span className="wvd-upload-hint">PNG, JPEG, or WebP · 2 MB max</span>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// DualRange — two-thumb range slider rendered as a single track.
//
// Stacks two transparent <input type="range"> elements so the browser
// handles thumb dragging natively (free keyboard support, free a11y).
// A separate fill div shows the selected band; the underlying track div
// shows the unselected band. Thumbs clamp at each other so the parent
// can't drag past — handles the edge case where one drag would invert
// min/max.
// ─────────────────────────────────────────────────────────────────────────
function DualRange({
  floor,
  ceil,
  min,
  max,
  onChange,
}: {
  floor: number;
  ceil: number;
  min: number;
  max: number;
  onChange: (lo: number, hi: number) => void;
}) {
  const range = Math.max(1, ceil - floor);
  const minPct = ((min - floor) / range) * 100;
  const maxPct = ((max - floor) / range) * 100;

  return (
    <div className="wvd-dual">
      {/* The bar is inset by one thumb-radius (11px) on each side so the
          thumb centers (which the browser places at the input's left/right
          edge at min/max) line up exactly with the visible fill band. */}
      <div className="wvd-dual-bar">
        <div className="wvd-dual-track" />
        <div
          className="wvd-dual-fill"
          style={{ left: `${minPct}%`, right: `${100 - maxPct}%` }}
        />
        <input
          type="range"
          className="wvd-dual-input"
          min={floor}
          max={ceil}
          step={1}
          value={min}
          onChange={(e) => {
            // Clamp min so it can't cross max.
            const v = Math.min(Number(e.target.value), max);
            onChange(v, max);
          }}
          aria-label="Minimum age"
        />
        <input
          type="range"
          className="wvd-dual-input"
          min={floor}
          max={ceil}
          step={1}
          value={max}
          onChange={(e) => {
            // Clamp max so it can't cross min.
            const v = Math.max(Number(e.target.value), min);
            onChange(min, v);
          }}
          aria-label="Maximum age"
        />
      </div>
      <div className="wvd-dual-ticks">
        <span>{floor}</span>
        <span>{ceil}</span>
      </div>
    </div>
  );
}

function WVDStyles() {
  return (
    <style>{`
      /* Compound selector to outrank the base .ws-modal-card width rule
         (which sets 640px). Same specificity + source-order would lose
         when StudioStyles renders later in the DOM tree. */
      .ws-modal-card.wvd-card {
        width: min(1200px, 96vw);
        max-width: min(1200px, 96vw);
        max-height: calc(100vh - 48px);
        overflow-y: auto;
        /* Defense in depth: never bleed past the card's right edge even
           if a grid child reports an intrinsic min-content wider than 1fr. */
        overflow-x: hidden;
        padding: 0;
      }
      .wvd-grid {
        display: grid;
        grid-template-columns: 400px minmax(0, 1fr);
        gap: 32px;
        padding: 28px 32px 28px;
      }
      /* Both tracks need min-width: 0 — grid's default minmax(auto, 1fr)
         lets children with long content push the track wider than its 1fr
         allocation, which spilled the description / prompt past the modal
         right edge (and clipped "Save changes"). */
      .wvd-poster-col, .wvd-fields {
        min-width: 0;
      }
      .wvd-poster-col {
        display: flex; flex-direction: column; gap: 12px;
      }
      .wvd-poster {
        position: relative;
        aspect-ratio: 16 / 9;
        border-radius: 14px;
        overflow: hidden;
        background: linear-gradient(135deg, #FFB774, #FF8A4C);
      }
      .wvd-poster img {
        width: 100%; height: 100%; object-fit: cover;
        display: block;
      }
      .wvd-poster-empty {
        width: 100%; height: 100%;
        display: grid; place-items: center;
      }
      .wvd-poster-play {
        position: absolute;
        bottom: 12px; right: 12px;
        width: 44px; height: 44px;
        border-radius: 50%;
        background: rgba(0,0,0,0.6);
        color: #fff; border: 0;
        cursor: pointer;
        display: grid; place-items: center;
      }
      .wvd-poster-play:hover { background: rgba(0,0,0,0.8); }
      .wvd-poster-actions {
        display: flex; gap: 6px;
      }
      .wvd-danger { color: #C44; }

      .wvd-poster-picker {
        border: 1.5px solid var(--primary);
        background: var(--bg-warm);
        border-radius: 12px;
        padding: 10px;
      }
      .wvd-picker-tabs {
        display: flex; gap: 6px; align-items: center;
        margin-bottom: 8px;
      }
      .wvd-picker-tab {
        padding: 4px 10px;
        border-radius: 999px;
        background: transparent;
        border: 0;
        font: inherit; font-weight: 800; font-size: 12px;
        color: var(--ink-3);
        cursor: pointer;
      }
      .wvd-picker-tab.active {
        background: var(--surface);
        color: var(--primary-ink);
      }
      .wvd-picker-tab:disabled { opacity: 0.4; cursor: not-allowed; }
      .wvd-picker-close {
        width: 22px; height: 22px;
        border-radius: 50%;
        border: 1.5px solid var(--line);
        background: var(--surface);
        color: var(--ink-2);
        cursor: pointer;
        font: inherit; font-weight: 900; font-size: 12px;
        line-height: 1;
      }
      .wvd-picker-empty {
        font-size: 12px; font-weight: 700;
        color: var(--ink-3);
        text-align: center;
        padding: 10px;
      }
      .wvd-frame-row {
        display: flex; gap: 6px;
        overflow-x: auto;
      }
      .wvd-frame-tile {
        flex: 0 0 auto;
        width: 86px; height: 50px;
        border-radius: 6px;
        border: 1.5px solid var(--line);
        background: var(--surface);
        cursor: pointer;
        overflow: hidden;
        padding: 0;
      }
      .wvd-frame-tile.loading {
        animation: wvd-pulse 1.2s ease-in-out infinite;
      }
      @keyframes wvd-pulse {
        0%,100% { background: var(--surface); }
        50%     { background: #FFD6B0; }
      }
      .wvd-frame-tile img {
        width: 100%; height: 100%; object-fit: cover;
      }
      .wvd-frame-tile:hover { border-color: var(--primary); }
      .wvd-upload-row {
        display: flex; align-items: center; gap: 10px;
        padding: 8px 4px;
      }
      .wvd-upload-hint {
        font-size: 11px; font-weight: 700; color: var(--ink-3);
      }

      .wvd-cta-row {
        display: flex; gap: 6px;
        margin-top: 4px;
      }

      .wvd-fields {
        display: flex; flex-direction: column; gap: 14px;
      }
      .wvd-eyebrow {
        font-size: 11px; font-weight: 900; letter-spacing: 0.14em;
        text-transform: uppercase;
        color: var(--primary-ink);
      }
      .wvd-field { display: flex; flex-direction: column; gap: 5px; }
      .wvd-field-label {
        font-size: 11px; font-weight: 800;
        letter-spacing: 0.10em; text-transform: uppercase;
        color: var(--ink-3);
      }
      .wvd-textarea {
        resize: vertical;
        min-height: 72px;
      }
      .wvd-chip-row {
        display: flex; flex-wrap: wrap; gap: 6px;
      }
      .wvd-chip {
        padding: 6px 12px;
        border-radius: 999px;
        background: var(--surface);
        border: 1.5px solid var(--line);
        font: inherit; font-weight: 800; font-size: 12.5px;
        color: var(--ink-2);
        cursor: pointer;
      }
      .wvd-chip:hover { border-color: var(--primary); }
      .wvd-chip.on {
        background: var(--ink);
        border-color: var(--ink);
        color: #fff;
      }
      /* ── Dual-thumb age range slider ─────────────────────────────────── */
      .wvd-dual {
        position: relative;
        margin-top: 6px;
        max-width: 380px;
      }
      /* Inset wrapper — 11px is the slider thumb radius. Everything inside
         (track, fill, both inputs) lives in this coordinate space so
         thumb centers and fill edges share the same 0%/100% origin. */
      .wvd-dual-bar {
        position: relative;
        height: 28px;
        margin: 0 11px;
      }
      /* Unselected track behind everything. */
      .wvd-dual-track {
        position: absolute;
        top: 50%; transform: translateY(-50%);
        left: 0; right: 0;
        height: 6px;
        background: var(--bg-warm);
        border-radius: 999px;
        pointer-events: none;
      }
      /* Selected band — left/right are set inline as percentages of the
         inset wrapper so the band aligns exactly with the thumb centers. */
      .wvd-dual-fill {
        position: absolute;
        top: 50%; transform: translateY(-50%);
        height: 6px;
        background: var(--primary);
        border-radius: 999px;
        pointer-events: none;
      }
      /* Both range inputs stack on the inset bar. Their tracks are
         invisible (transparent + pointer-events: none) so only the thumbs
         catch clicks — that lets two native range inputs share one bar. */
      .wvd-dual-input {
        position: absolute;
        top: 0;
        left: 0; right: 0;
        width: 100%;
        height: 28px;
        background: transparent;
        appearance: none;
        -webkit-appearance: none;
        margin: 0;
        pointer-events: none;
      }
      .wvd-dual-input::-webkit-slider-runnable-track {
        background: transparent;
        height: 6px;
      }
      .wvd-dual-input::-moz-range-track {
        background: transparent;
        height: 6px;
      }
      .wvd-dual-input::-webkit-slider-thumb {
        pointer-events: auto;
        appearance: none;
        -webkit-appearance: none;
        width: 22px; height: 22px;
        border-radius: 50%;
        background: var(--primary);
        border: 3px solid var(--surface);
        cursor: pointer;
        box-shadow: 0 2px 6px rgba(0,0,0,0.18);
        position: relative;
        z-index: 2;
        /* Webkit positions the thumb's TOP at the runnable-track's top by
           default — for our 22px thumb on a 6px track that drops the thumb
           below the centerline. Pull it up by (thumb - track) / 2 = 8px
           so the thumb center sits on the track center. */
        margin-top: -8px;
      }
      .wvd-dual-input::-moz-range-thumb {
        pointer-events: auto;
        appearance: none;
        width: 22px; height: 22px;
        border-radius: 50%;
        background: var(--primary);
        border: 3px solid var(--surface);
        cursor: pointer;
        box-shadow: 0 2px 6px rgba(0,0,0,0.18);
      }
      .wvd-dual-ticks {
        display: flex; justify-content: space-between;
        margin-top: 6px;
        padding: 0 11px;
        font-size: 11px; font-weight: 800;
        letter-spacing: 0.06em;
        color: var(--ink-3);
        font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
      }
      .wvd-error {
        font-size: 12.5px; font-weight: 700;
        color: #C0392B;
        background: rgba(192,57,43,0.08);
        padding: 8px 10px;
        border-radius: 8px;
      }
      .wvd-actions {
        display: flex; justify-content: flex-end; gap: 8px;
        margin-top: 4px;
      }

      @media (max-width: 720px) {
        .wvd-grid {
          grid-template-columns: 1fr;
        }
      }
    `}</style>
  );
}

export default WebVideoDetailsEditor;
